import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Form, Formik, useFormikContext } from "formik";
import FormControl from "../components/form/FormControl";
import SubmitButton from "../components/SubmitButton";
import DashboardMessage from "../components/DashboardMessage";
import Loader from "../components/Loader";
import useFetch from "../customHooks/useFetch";
import usePost from "../customHooks/usePost";
import usePut from "../customHooks/usePut";
import http from "../services/httpServices";
import config from "../services/config.json";
import { getTokenSession } from "../utils/common";
import { MenuActive } from "../context/MenuActiveContext";
import { toast } from "react-toastify";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const Documents = ({ admin = false }) => {
  const { setMunuActiv } = useContext(MenuActive);
  const { loading, data } = useFetch(
    admin ? "dashboard/documents" : "documents",
  );
  const [documents, setDocuments] = useState([]);
  useEffect(() => {
    setMunuActiv([true, "Documents"]);
  }, [setMunuActiv]);
  useEffect(() => {
    const result = data?.data;
    setDocuments(Array.isArray(result) ? result : result ? [result] : []);
  }, [data]);
  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await http.delete(`${config.apiEndPoint}dashboard/documents/${id}`, {
        headers: { Authorization: `Bearer ${getTokenSession()}` },
      });
      setDocuments((items) => items.filter((item) => item.id !== id));
      toast.success("Document deleted");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to delete document");
    }
  };
  if (loading) return <Loader />;
  return (
    <div className="p-6">
      {admin && (
        <DashboardMessage
          data={{
            button: {
              label: "Create Document",
              link: `/${config.demo}documents/create`,
            },
          }}
        />
      )}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h1 className="text-2xl font-bold mb-4">Documents</h1>
        {documents.map((item) => (
          <article
            key={item.id}
            className="border border-gray-200 rounded-lg p-4 mb-3 last:mb-0 hover:shadow-sm transition-shadow"
          >
            <div className="flex justify-between items-center gap-4">
              <div className="min-w-0"><h2 className="text-lg font-semibold text-gray-800 truncate">{item.title}</h2><p className="text-xs text-gray-500 truncate">/{item.slug}</p></div>
              {admin && (
                <div className="flex gap-3 text-sm whitespace-nowrap">
                  <Link
                    className="text-blue-600"
                    to={`/${config.demo}documents/edit/${item.id}`}
                  >
                    Edit
                  </Link>
                  <button
                    className="text-red-600"
                    onClick={() => remove(item.id)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
            {admin ? (
              <p className="text-sm text-gray-500 mt-3 line-clamp-2">
                {(item.body || "").replace(/<[^>]*>/g, "").trim() || "No content"}
              </p>
            ) : (
              <div className="text-gray-600 mt-3" dangerouslySetInnerHTML={{ __html: item.body || "" }} />
            )}
            <p className="text-xs text-gray-400 mt-2">Updated {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "—"}</p>
          </article>
        ))}
        {!documents.length && (
          <p className="text-gray-500">No documents found.</p>
        )}
      </div>
    </div>
  );
};

export const DocumentForm = ({ edit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setMunuActiv } = useContext(MenuActive);
  const [document, setDocument] = useState(null);
  const { loading, data } = useFetch(
    edit ? `dashboard/documents/${id}` : "dashboard/documents",
  );
  const [post, create] = usePost();
  const [put, update] = usePut();
  useEffect(() => setMunuActiv([true, "Documents"]), [setMunuActiv]);
  useEffect(() => {
    if (edit)
      setDocument(Array.isArray(data?.data) ? data.data[0] : data?.data);
  }, [edit, data]);
  const initialValues = {
    slug: document?.slug || "",
    title: document?.title || "",
    body: document?.body || "",
  };
  const submit = (values) => {
    const payload = new FormData();
    payload.append("slug", values.slug);
    payload.append("title", values.title);
    payload.append("body", values.body);
    edit
      ? update(`dashboard/documents/${id}`, payload)
      : create("dashboard/documents", payload);
  };
  useEffect(() => {
    if (post.data || put.data) {
      toast.success((post.data || put.data).message || "Document saved");
      navigate(`/${config.demo}documents`);
    }
  }, [post.data, put.data, navigate]);
  if (loading) return <Loader />;
  return (
    <div className="p-6">
      <Link to={`/${config.demo}documents`} className="text-blue-600">
        ← Back to Documents
      </Link>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={submit}
      >
        <FormikInnerForm initialValues={initialValues} submit={submit} edit={edit} loading={post.isLoading || put.isLoading} />
      </Formik>
    </div>
  );
};

const FormikInnerForm = ({ edit, loading }) => {
  const { values, setFieldValue } = useFormikContext();
  return <Form className="bg-white p-6 mt-5 rounded-xl">
    <FormControl name="slug" label="Slug" placeholder="terms-and-conditions" control="input" />
    <FormControl name="title" label="Title" placeholder="Document title" control="input" />
    <div className="mt-4"><label className="block mb-2 font-medium">Body</label><CKEditor class="h-[50rem]" editor={ClassicEditor} data={values.body} onChange={(_, editor) => setFieldValue("body", editor.getData())} /></div>
    <SubmitButton props={{ class: "btn btn-primary block mt-5 submit", text: edit ? "Update Document" : "Create Document" }} buttonLoading={loading} />
  </Form>;
};

export default Documents;
